import React, {
  useState, useEffect, useCallback, useMemo,
} from 'react';
import {useSelector, useDispatch, shallowEqual} from 'react-redux';
import PropTypes from 'prop-types';
import axios from 'axios';
import {cacheAdapterEnhancer, throttleAdapterEnhancer} from 'axios-extensions';
import _ from 'lodash';
import {scrollTo} from 'scroll-js';
import classnames from 'classnames';
import {OptionalBrowserRouter} from '../../helpers/routerCompat';

import * as actionCreators from '../../store/actions';
import ReactHookTableWrapper from '../../helpers/ReactHookTableWrapper';
import {getQueryParam, setQueryParam} from '../../helpers/common';
import useYearlyDiscountPercent from '../../helpers/hooks/useYearlyDiscountPercent';
import LoginLink from './LoginLink';
import utilities from '../../utilities/utilities';
import utilitiesGTM from '../../utilities/utilitiesGTM';
import PopupBase from './PopupBase';
import ContactUsPopup from './ContactUsPopup';
import getAxiosInstance, {BASE_URL_TYPE} from '../../api/axios';

// Create `axios` instance passing the newly created `cache.adapter` (for GET subscriptions).
const api = axios.create({
  adapter: throttleAdapterEnhancer(cacheAdapterEnhancer(axios.getAdapter(axios.defaults.adapter))),
});

// Axios instance with CSRF interceptor for authenticated POSTs (e.g. Stripe checkout).
const propzoneAxios = getAxiosInstance(BASE_URL_TYPE.PROPZONE, false, true);

const propTypes = {
  isPopup: PropTypes.bool,
  hideGeneralPreview: PropTypes.bool,
  toggleCompare: PropTypes.bool,
  toggleContactModal: PropTypes.instanceOf(Object),
  loadedData: PropTypes.instanceOf(Object),
  billingCycle: PropTypes.string,
  onBillingCycleChange: PropTypes.func,

  ctaButtonText: PropTypes.string,
  ctaButtonHandler: PropTypes.func,
  ctaButtonGtmData: PropTypes.instanceOf(Object),
  ctaActiveSection: PropTypes.string,
};

function SubscriptionsComponent(props) {
  // Props.
  const {
    isPopup = true,
    hideGeneralPreview = false,
    toggleCompare = false,
    toggleContactModal: [contactModal, setContactModal] = [false, () => {}],
    loadedData = {},
    billingCycle: billingCycleProp = null,
    onBillingCycleChange = null,
    ctaButtonText = 'Download Now',
    ctaButtonHandler = null,
    ctaButtonGtmData = null,
    ctaActiveSection = null,
  } = props;

  // States.
  const [subscriptionsData, setSubscriptionsData] = useState(loadedData);
  const [billingCycleInternal, setBillingCycleInternal] = useState('yearly');
  const billingCycle = billingCycleProp != null ? billingCycleProp : billingCycleInternal;
  const setBillingCycle = useCallback(
    (value) => {
      if (onBillingCycleChange) {
        onBillingCycleChange(value);
      }
      else {
        setBillingCycleInternal(value);
      }
    },
    [onBillingCycleChange],
  );
  const [showCompare, setShowCompare] = useState(false);
  const [showContactPopup, setShowContactPopup] = useState(false);
  const [stripeCheckoutLoading, setStripeCheckoutLoading] = useState(null);
  // Get redux states.
  const {userData, isMobile, mapData} = useSelector(state => ({
    userData: state.user.userData,
    isMobile: state.ui.isMobile,
    mapData: state.map.mapData,
  }), shallowEqual);

  // Set dispatchers.
  const dispatch = useDispatch();
  const setLoading = useCallback(state => dispatch(actionCreators.setLoading(state)), [dispatch]);

  const getData = useCallback(() => {
    const requestUrl = `${BASE_URL_TYPE.PROPZONE}/api/v1/subscriptions`;
    setLoading(true);
    api
      .get(requestUrl, {
        withCredentials: true,
      })
      .then((response) => {
        const data = _.get(response, 'data', {});
        setLoading(false);
        setSubscriptionsData(data);
      })
      .catch((e) => {
        console.error(e);
        setLoading(false);
      });
  }, [setLoading]);

  useEffect(() => {
    if (_.isEmpty(subscriptionsData)) {
      getData();
    }
  }, [getData, subscriptionsData]);

  useEffect(() => {
    if (!isPopup) {
      return undefined;
    }
    if (!getQueryParam('subscriptionPopup')) {
      setQueryParam('subscriptionPopup', '1');
    }
    return () => {
      if (getQueryParam('subscriptionPopup')) {
        setQueryParam('subscriptionPopup', null, 2);
      }
    };
  }, [isPopup]);

  const scrollFunc = useCallback(
    (plan = false) => {
      const $compareWrapper = _.isString(plan) && isMobile
        ? document.getElementById(`table-plan-${plan}`)
        : document.getElementById('compare-wrapper');
      if ($compareWrapper) {
        const $parent = isPopup ? document.getElementById('popup-container') : document.getElementById('main');
        const {offsetTop} = $compareWrapper;
        const extraOffset = isPopup ? document.querySelector('#popup-container .popup').offsetTop : 0;
        scrollTo($parent, {top: offsetTop - (isMobile ? 40 : 70) + extraOffset});
      }
    },
    [isMobile, isPopup],
  );

  useEffect(() => {
    if (showCompare) {
      scrollFunc(showCompare);
    }
  }, [isPopup, scrollFunc, showCompare]);

  const scrollToCompare = useCallback(
    (e, plan = false) => {
      e.preventDefault();
      setShowCompare(plan || true);
      if (showCompare) {
        scrollFunc(plan);
      }
    },
    [scrollFunc, showCompare],
  );

  const questionPopup = useCallback(
    (e) => {
      e.preventDefault();
      setShowContactPopup(true);
    },
    [setShowContactPopup],
  );

  const sortedPlans = useMemo(() => {
    const plansObj = _.get(subscriptionsData, ['configured_plans'], {});
    const entries = _.sortBy(
      Object.entries(plansObj).map(([key, plan]) => ({...plan, planKey: key})),
      item => parseInt(_.get(item, ['weight'], 0), 10),
    );
    return entries;
  }, [subscriptionsData]);

  const filteredPlans = useMemo(
    () => sortedPlans.filter(plan => !plan.billing_cycle || plan.billing_cycle === billingCycle),
    [sortedPlans, billingCycle],
  );

  const showBillingSwitcher = useMemo(
    () => _.some(sortedPlans, plan => plan.billing_cycle === 'yearly' || plan.billing_cycle === 'monthly'),
    [sortedPlans],
  );

  const yearlyDiscountPercent = useYearlyDiscountPercent(sortedPlans);

  const hasAnyPlan = useMemo(
    () => _.find(sortedPlans, plan => _.get(userData, ['licenses', `propzone-${plan.access_level}`])),
    [sortedPlans, userData],
  );

  const priceFormat = plan => (
    <div className="price">
      {plan.price && <span className="value">{plan.price.replace('.00', '')}</span>}
      {plan.price_suffix && <span className="suffix">{` / ${plan.price_suffix}`}</span>}
    </div>
  );

  const handleStripeUpgrade = useCallback(
    async (plan) => {
      if (!plan.sku) return;
      setStripeCheckoutLoading(plan.planKey);
      try {
        const base = `${window.location.origin}${window.location.pathname}`;
        const successUrl = `${base}?success_session_id={CHECKOUT_SESSION_ID}&checkout_type=subscription${
          mapData.place_id ? `&place_id=${mapData.place_id}` : ''
        }`;
        const cancelUrl = `${base}?cancel_session_id={CHECKOUT_SESSION_ID}&checkout_type=subscription`;
        const response = await propzoneAxios.post(`${BASE_URL_TYPE.PROPZONE}/api/v1/stripe/create-checkout-session`, {
          mode: 'subscription',
          price_id: plan.sku,
          quantity: 1,
          success_url: successUrl,
          cancel_url: cancelUrl,
        });
        const {checkout_url: checkoutUrl} = response.data;
        sessionStorage.setItem('stripeCheckoutUrl', checkoutUrl);
        window.location.href = checkoutUrl;
      }
      catch (err) {
        console.error('Stripe checkout error:', err);
        setStripeCheckoutLoading(null);
      }
    },
    [mapData.place_id],
  );

  const getButton = useCallback(
    (plan) => {
      const isLoggedIn = utilities.isLoggedIn(userData);
      let button = '';
      switch (plan.access_level) {
        case 'anonymous':
          button = '';
          break;

        case 'basic':
          if (isLoggedIn) {
            const hasOthePlan = _.find(
              _.get(userData, ['licenses'], []),
              (license, licenseName) => licenseName.indexOf('propzone') > -1,
            );
            button = (
              <button type="button" className="btn medium green">
                {hasOthePlan ? 'Included' : 'Active'}
              </button>
            );
          }
          else {
            button = <LoginLink className="btn medium" text="Get started" />;
          }
          break;
        case 'enterprise':
          button = (
            <button type="button" className="btn medium" onClick={questionPopup}>
              Contact Us
            </button>
          );
          break;
        default: {
          const hasCurrentPlan = _.get(userData, ['licenses', `propzone-${plan.access_level}`]);
          if (plan.sku && !hasCurrentPlan && !hasAnyPlan) {
            if (isLoggedIn) {
              button = (
                <button
                  type="button"
                  className="btn medium"
                  onClick={() => handleStripeUpgrade(plan)}
                  disabled={!!stripeCheckoutLoading}
                >
                  {stripeCheckoutLoading === plan.planKey ? 'Processing...' : 'Upgrade Now'}
                </button>
              );
            }
            else {
              const currentPageUrl = `${window.location.origin}${window.location.pathname}${
                window.location.search ? `${window.location.search}&` : '?'
              }sku=${encodeURIComponent(plan.sku)}`;
              const url = `${process.env.REACT_APP_ACCOUNTS_SITE}/user/login?app=propzone&sku=${plan.sku}&place_id=${
                mapData.place_id
              }&destination-url=${encodeURIComponent(currentPageUrl)}`;
              button = <LoginLink className="btn medium" urlType="subscribe" text="Upgrade Now" url={url} />;
            }
          }
          else if (!hasCurrentPlan && hasAnyPlan) {
            button = (
              <button type="button" className="btn medium" onClick={questionPopup}>
                Contact Us
              </button>
            );
          }
          else if (hasCurrentPlan) {
            button = (
              <button type="button" className="btn medium green">
                Active
              </button>
            );
          }
        }
      }
      return button;
    },
    [hasAnyPlan, handleStripeUpgrade, mapData.place_id, questionPopup, stripeCheckoutLoading, userData],
  );

  useEffect(() => {
    if (!utilities.isLoggedIn(userData) || !sortedPlans.length) return;
    const sku = getQueryParam('sku', window.location.href);
    if (!sku) return;
    const plan = _.find(sortedPlans, p => p.sku === sku);
    if (!plan) return;
    setQueryParam('sku', null, 2);
    if (hasAnyPlan) return;
    handleStripeUpgrade(plan);
  }, [sortedPlans, userData, hasAnyPlan, handleStripeUpgrade]);

  const plans = useMemo(() => {
    const elements = [];
    _.each(filteredPlans, (plan) => {
      const element = (
        <div key={plan.planKey} className={classnames('plan', {'most-popular': plan.most_popular})}>
          {plan.image_path && (
            <div className="plan-logo">
              <img src={BASE_URL_TYPE.PROPZONE + plan.image_path} alt={plan.title} />
            </div>
          )}
          <div className="name">{plan.title}</div>
          {!_.isEmpty(plan.most_popular) && <div className="most-popular-label">Most popular plan</div>}
          {priceFormat(plan)}
          <div className="short-desc">{plan.short_description}</div>
          <div className="desc">
            {plan.description.split('\n').map((item, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <p key={`paragraph-${i}`}>{item}</p>
            ))}
          </div>
          <div className="see-all">
            <a href="#compare-wrapper" onClick={e => scrollToCompare(e, plan.planKey)}>
              See all features
              <i className="icon-b icon-b-long-arrow-right" />
            </a>
          </div>
          <div className="button-wrapper">{getButton(plan)}</div>
        </div>
      );
      elements.push(element);
    });
    return elements;
  }, [getButton, scrollToCompare, filteredPlans]);

  const comparePlansTable = useMemo(() => {
    const prepareTable = (currentPlanKey = false) => {
      const data = [];
      const columns = [
        {
          Header: '',
          accessor: 'rowName',
        },
      ];
      // Prepare header.
      _.each(filteredPlans, (plan) => {
        if (currentPlanKey && plan.planKey !== currentPlanKey) {
          return;
        }
        const column = {
          Header: plan.title,
          accessor: plan.planKey,
        };
        columns.push(column);
      });
      // Prepare data. Compare table is keyed by access_level (basic, premium, enterprise);
      // map to our column accessors (planKey) so each filtered plan gets the right cell.
      _.each(_.get(subscriptionsData, 'compare_table', []), (row) => {
        const rowType = row.row_type;
        const dataRow = {};
        dataRow.rowName = row.row_name;
        const rowPlans = row.plans || {};
        _.each(filteredPlans, (plan) => {
          if (currentPlanKey && plan.planKey !== currentPlanKey) {
            return;
          }
          const cell = rowPlans[plan.access_level];
          if (cell == null) {
            return;
          }
          if (rowType === 'checkbox') {
            dataRow[plan.planKey] = cell.checkbox ? (
              <i className="icon-b icon-b-check" />
            ) : (
              isMobile && <i className="icon-b icon-b-close" />
            );
          }
          else {
            dataRow[plan.planKey] = cell.text;
          }
        });
        dataRow.rowClass = utilities.cleanCssIdentifier(rowType);
        data.push(dataRow);
      });

      // Prepare extra non-standard rows based on plans info.
      const priceRow = {
        rowName: 'Price',
        rowClass: 'group-strong price',
      };
      const planRow = {
        rowName: 'Plan',
        rowClass: 'group-strong plan',
      };
      const buttonsRow = {
        rowName: '',
        rowClass: 'group-strong price',
      };
      _.each(filteredPlans, (plan) => {
        if (currentPlanKey && plan.planKey !== currentPlanKey) {
          return;
        }
        priceRow[plan.planKey] = priceFormat(plan);
        planRow[plan.planKey] = <strong>{plan.title}</strong>;
        buttonsRow[plan.planKey] = getButton(plan);
      });
      data.unshift(priceRow);
      data.push(planRow, priceRow, buttonsRow);
      return <ReactHookTableWrapper data={data} columns={columns} />;
    };
    if (isMobile) {
      const tables = [];
      _.each(filteredPlans, (plan) => {
        tables.push(
          <div key={plan.planKey} className="table-plan" id={`table-plan-${plan.planKey}`}>
            <h3>{plan.title}</h3>
            {prepareTable(plan.planKey)}
          </div>,
        );
      });
      return tables;
    }

    return prepareTable();
  }, [getButton, isMobile, filteredPlans, subscriptionsData]);

  if (!filteredPlans.length) {
    return '';
  }
  const popupHeader = !ctaButtonHandler ? (
    'Choose the plan that’s right for you'
  ) : (
    <>
      {ctaButtonHandler && ctaButtonGtmData && (
        <div className="popup-header-download">
          <div className="subtitle">Choose the plan that’s right for you</div>
          <div className="separator">Or</div>
          <button
            type="button"
            className="download-link btn"
            onClick={(e) => {
              if (window.dataLayer) {
                utilitiesGTM.gtmDataLayerPushEvent(
                  ctaButtonGtmData.EVENT,
                  ctaButtonGtmData.BANNER_GTM_DATA.category,
                  ctaButtonGtmData.BANNER_GTM_DATA.action,
                  ctaButtonGtmData.SECTIONS[ctaActiveSection].LEARN_MORE,
                  userData.uid ? userData.uid : 'null',
                );
              }
              ctaButtonHandler(e);
            }}
          >
            {ctaButtonText}
          </button>
        </div>
      )}
    </>
  );

  const content = (
    <React.Fragment>
      <div className="subscriptions-wrapper">
        {!hideGeneralPreview && (
          <React.Fragment>
            {!isPopup && (
              <div className="title-wrapper">
                <h2>Choose the plan that’s right for you</h2>
              </div>
            )}
            <div className="plans-wrapper">
              {showBillingSwitcher && (
                <div className="billing-cycle-wrapper">
                  <div className="billing-cycle-switcher">
                    <button
                      type="button"
                      className={classnames('billing-cycle-option', {active: billingCycle === 'monthly'})}
                      onClick={() => setBillingCycle('monthly')}
                    >
                      Monthly
                    </button>
                    <button
                      type="button"
                      className={classnames('billing-cycle-option', {active: billingCycle === 'yearly'})}
                      onClick={() => setBillingCycle('yearly')}
                    >
                      Yearly
                    </button>
                  </div>
                  {yearlyDiscountPercent != null && (
                    <span className="billing-cycle-savings">{`Save ${yearlyDiscountPercent}%`}</span>
                  )}
                </div>
              )}
              <div className="plans">{plans}</div>
              <div className="see-all">
                <a href="#compare-wrapper" onClick={scrollToCompare}>
                  Compare plans and see all features
                  <i className="icon-b icon-b-long-arrow-right" />
                </a>
              </div>
            </div>
          </React.Fragment>
        )}
        {(showCompare || toggleCompare) && (
          <div className="compare-wrapper" id="compare-wrapper">
            <h2 className="compare-title">Compare plans</h2>
            <div className="message">Unsure which plan is right for you? Here’s the full breakdown.</div>
            {comparePlansTable}
          </div>
        )}
      </div>
      {(showContactPopup || contactModal) && (
        <div id="subscription-popup">
          <ContactUsPopup
            defaultMessage="I would like to be contacted to help upgrade my account."
            closeEvent={() => {
              setShowContactPopup(false);
              setContactModal(false);
            }}
          />
        </div>
      )}
    </React.Fragment>
  );

  if (isPopup) {
    return (
      <PopupBase onEscape={() => !showContactPopup} className="subscriptions" titleHtml="" title={popupHeader}>
        <OptionalBrowserRouter>{content}</OptionalBrowserRouter>
      </PopupBase>
    );
  }
  return content;
}

SubscriptionsComponent.propTypes = propTypes;

export default SubscriptionsComponent;
